<template>
	<!-- This component doesn't have visual representation, it only applies styles dynamically -->
	<div style="display: none"></div>
</template>

<script>
export default {
	name: "VendorStyle",
	props: {
		vendor: {
			type: Object,
			required: true,
		},
	},
	data() {
		return {
			styleId: "vendor-custom-style",
			defaultFont: "Roboto, sans-serif", // fallback font
			defaultPrimaryColor: "#1976D2", // fallback primary color (Vuetify default blue)
			defaultSecondaryColor: "#424242", // fallback secondary color
			defaultAccentColor: "#82B1FF", // fallback accent color
		};
	},
	watch: {
		vendor: {
			immediate: true,
			handler(newVendor) {
				this.applyVendorStyles(newVendor);
			},
		},
	},
	methods: {
		applyVendorStyles(vendor) {
			if (!vendor) return;

			// Create or get the style element
			let styleElement = document.getElementById(this.styleId);
			if (!styleElement) {
				styleElement = document.createElement("style");
				styleElement.id = this.styleId;
				document.head.appendChild(styleElement);
			}

			// Create the CSS rules
			const cssRules = this.generateCssRules(vendor);

			// Apply the CSS rules
			styleElement.innerHTML = cssRules;

			// If vendor has a primary font, load it
			if (vendor.primaryFont) {
				this.loadFontIfNeeded(vendor.primaryFont);
			}
		},

		generateCssRules(vendor) {
			let rules = [];

			// Apply font family if available
			if (vendor.primaryFont) {
				rules.push(`
                    body, .v-application {
                        font-family: ${vendor.primaryFont}, ${this.defaultFont} !important;
                    }
                    
                    .v-application .headline, 
                    .v-application .title, 
                    .v-application .subtitle-1, 
                    .v-application .subtitle-2 {
                        font-family: ${vendor.primaryFont}, ${this.defaultFont} !important;
                    }
                `);
			}

			// Apply primary color if available
			if (vendor.primaryColor) {
				rules.push(`
                    .v-application .primary {
                        background-color: ${vendor.primaryColor} !important;
                        border-color: ${vendor.primaryColor} !important;
                    }
                    
                    .v-application .primary--text {
                        color: ${vendor.primaryColor} !important;
                        caret-color: ${vendor.primaryColor} !important;
                    }
                    
                    .theme--light.v-btn.v-btn--has-bg.primary {
                        background-color: ${vendor.primaryColor} !important;
                    }
                    
                    .v-application a {
                        color: ${vendor.primaryColor};
                    }
                `);
			}

			// Apply secondary color if available
			if (vendor.secondaryColor) {
				rules.push(`
                    .v-application .secondary {
                        background-color: ${vendor.secondaryColor} !important;
                        border-color: ${vendor.secondaryColor} !important;
                    }
                    
                    .v-application .secondary--text {
                        color: ${vendor.secondaryColor} !important;
                        caret-color: ${vendor.secondaryColor} !important;
                    }
                    
                    .theme--light.v-btn.v-btn--has-bg.secondary {
                        background-color: ${vendor.secondaryColor} !important;
                    }
                `);
			}

			// Apply accent color if available
			if (vendor.accentColor) {
				rules.push(`
                    .v-application .accent {
                        background-color: ${vendor.accentColor} !important;
                        border-color: ${vendor.accentColor} !important;
                    }
                    
                    .v-application .accent--text {
                        color: ${vendor.accentColor} !important;
                        caret-color: ${vendor.accentColor} !important;
                    }
                    
                    .theme--light.v-btn.v-btn--has-bg.accent {
                        background-color: ${vendor.accentColor} !important;
                    }
                    
                    .v-application .v-btn--text:hover::before,
                    .v-application .v-btn--outlined:hover::before {
                        opacity: 0.08;
                        background-color: ${vendor.accentColor} !important;
                    }
                `);
			}

			return rules.join("\n");
		},

		loadFontIfNeeded(fontFamily) {
			// Extract the first font from the font-family string
			const primaryFont = fontFamily.split(",")[0].trim();

			// Check if it's a web font that might need loading
			if (!this.isSystemFont(primaryFont)) {
				// For now, we'll just use Google Fonts as an example
				// In a real implementation, you might want to support multiple font providers
				// or have a mapping of vendors to their font requirements

				// Check if Google Fonts is already loaded for this font
				const googleFontLink = document.querySelector(
					`link[href*="fonts.googleapis.com"][href*="${primaryFont}"]`
				);
				if (!googleFontLink) {
					const link = document.createElement("link");
					link.rel = "stylesheet";
					link.href = `https://fonts.googleapis.com/css2?family=${primaryFont.replace(
						/ /g,
						"+"
					)}&display=swap`;
					document.head.appendChild(link);
				}
			}
		},

		isSystemFont(font) {
			// List of common system fonts that don't need to be loaded from web
			const systemFonts = [
				"Arial",
				"Helvetica",
				"Times New Roman",
				"Times",
				"Courier New",
				"Courier",
				"Verdana",
				"Georgia",
				"Palatino",
				"Garamond",
				"Bookman",
				"Tahoma",
				"Trebuchet MS",
				"Arial Black",
				"Impact",
				"Comic Sans MS",
			];

			return systemFonts.some((systemFont) => systemFont.toLowerCase() === font.toLowerCase());
		},
	},
	beforeDestroy() {
		// Clean up by removing the style element when component is destroyed
		const styleElement = document.getElementById(this.styleId);
		if (styleElement) {
			styleElement.parentNode.removeChild(styleElement);
		}
	},
};
</script>

<style scoped>
/* No scoped styles needed for this component */
</style>
